<script lang="ts">
    import Fa from 'svelte-fa'
    import {
        faDownload,
        faFile,
        faFileExcel,
        faFileImage,
        faFileLines,
        faFilePdf,
        faFilePowerpoint,
        faFileWord,
        faTriangleExclamation,
        faUpload,
        faXmark,
    } from '@fortawesome/free-solid-svg-icons'
    import FieldHeading from './field-heading.svelte'

    interface UploadedFile {
        id: string
        name: string
        size: number
        type: string
        dataUrl: string
    }

    export let label: string
    export let data: Record<string, any>
    export let value: UploadedFile[] = []
    export let required = false
    export let readOnly = false

    // Allow-list: só formatos de documento/imagem do dia a dia. Qualquer extensão fora
    // desta lista é rejeitada no cliente (executáveis, scripts, instaladores, etc. nunca passam).
    // Validação real de conteúdo/segurança deve ser repetida no backend quando o upload existir.
    const ALLOWED_EXTENSIONS = [
        'pdf',
        'doc',
        'docx',
        'xls',
        'xlsx',
        'ppt',
        'pptx',
        'txt',
        'csv',
        'rtf',
        'odt',
        'ods',
        'odp',
        'png',
        'jpg',
        'jpeg',
        'gif',
        'webp',
        'bmp',
        'heic',
    ]

    /**
     * Teto por arquivo. Não há limite de tamanho em nenhum outro ponto do caminho até o servidor:
     * o conteúdo é lido inteiro como base64 (que ainda infla ~33%) e só encontraria barreira nos
     * 50 MB de corpo de requisição da API — tarde, depois da espera, e com erro cru.
     * Imagens não passam por aqui: os blocos de foto reduzem a resolução no próprio upload.
     */
    const MAX_FILE_BYTES = 5 * 1024 * 1024

    let fileInputEl: HTMLInputElement
    let rejectedNames: string[] = []
    // Separado de `rejectedNames` porque o motivo é outro: o arquivo é de um tipo aceito, só está
    // grande demais. Juntar os dois diria "não permitido" para um PDF perfeitamente válido.
    let oversizedNames: string[] = []

    function getExtension(name: string) {
        return name.split('.').pop()?.toLowerCase() ?? ''
    }

    function isAllowed(name: string) {
        return ALLOWED_EXTENSIONS.includes(getExtension(name))
    }

    function getFileIcon(name: string) {
        const ext = getExtension(name)
        if (ext === 'pdf') return faFilePdf
        if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return faFileWord
        if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) return faFileExcel
        if (['ppt', 'pptx', 'odp'].includes(ext)) return faFilePowerpoint
        if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic'].includes(ext)) return faFileImage
        if (ext === 'txt') return faFileLines
        return faFile
    }

    function formatSize(bytes: number) {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    function onFilesSelected(event: Event) {
        if (readOnly) return
        const input = event.currentTarget as HTMLInputElement
        const files = input.files ? Array.from(input.files) : []
        const rejected: string[] = []
        const oversized: string[] = []

        for (const file of files) {
            if (!isAllowed(file.name)) {
                rejected.push(file.name)
                continue
            }
            // Antes de ler: o FileReader carrega o arquivo inteiro na memória da aba, e num celular
            // um arquivo grande derruba a página antes mesmo de chegar ao servidor.
            if (file.size > MAX_FILE_BYTES) {
                oversized.push(file.name)
                continue
            }
            const reader = new FileReader()
            reader.onload = () => {
                value = [
                    ...value,
                    {
                        id: crypto.randomUUID(),
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        dataUrl: reader.result as string,
                    },
                ]
            }
            reader.readAsDataURL(file)
        }

        rejectedNames = rejected
        oversizedNames = oversized
        input.value = ''
    }

    function removeFile(id: string) {
        if (readOnly) return
        value = value.filter((file) => file.id !== id)
    }

    function downloadFile(file: UploadedFile) {
        const link = document.createElement('a')
        link.href = file.dataUrl
        link.download = file.name
        link.click()
    }
</script>

<div class="file-upload-field">
    <FieldHeading {label} {required} {readOnly} />

    {#if !readOnly}
        <div class="upload-row">
            <button type="button" class="upload-btn" on:click={() => fileInputEl.click()}>
                <Fa icon={faUpload} />
                <span>Selecionar arquivos</span>
            </button>
            <span class="upload-hint">PDF, Word, Excel, PowerPoint, imagens, txt, csv...</span>
        </div>
        <input
            bind:this={fileInputEl}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.ods,.odp,.png,.jpg,.jpeg,.gif,.webp,.bmp,.heic"
            hidden
            on:change={onFilesSelected}
        />
    {/if}

    {#if rejectedNames.length > 0}
        <div class="upload-error">
            <Fa icon={faTriangleExclamation} />
            <span>Arquivo(s) não permitido(s): {rejectedNames.join(', ')}</span>
        </div>
    {/if}

    <!-- Só aparece depois da recusa: sem isso o arquivo sumiria em silêncio e a pessoa tentaria de
         novo o mesmo arquivo. O limite não é anunciado antes, de propósito. -->
    {#if oversizedNames.length > 0}
        <div class="upload-error">
            <Fa icon={faTriangleExclamation} />
            <span>Acima do limite de {formatSize(MAX_FILE_BYTES)} por arquivo: {oversizedNames.join(', ')}</span>
        </div>
    {/if}

    {#if value.length > 0}
        <ul class="file-list">
            {#each value as file (file.id)}
                <li class="file-row">
                    <button type="button" class="file-info" on:click={() => downloadFile(file)} title="Clique para baixar">
                        <Fa icon={getFileIcon(file.name)} />
                        <span class="file-name">{file.name}</span>
                        <span class="file-size">{formatSize(file.size)}</span>
                        <Fa icon={faDownload} class="download-icon" />
                    </button>
                    {#if !readOnly}
                        <button type="button" class="file-remove" title="Remover" on:click={() => removeFile(file.id)}>
                            <Fa icon={faXmark} />
                        </button>
                    {/if}
                </li>
            {/each}
        </ul>
    {:else}
        <p class="empty-hint">Nenhum arquivo adicionado ainda.</p>
    {/if}
</div>

<style>
    .upload-row {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }
    .upload-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #ced4da;
        background: #fff;
        color: #495057;
        border-radius: 8px;
        padding: 8px 14px;
        font-size: 0.85rem;
        cursor: pointer;
    }
    .upload-btn:hover {
        border-color: var(--cs2-accent, #4263eb);
        color: var(--cs2-accent, #4263eb);
    }
    .upload-hint {
        font-size: 0.74rem;
        color: #adb5bd;
    }
    .upload-error {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 10px;
        background: #fff5f5;
        border: 1px solid #ffc9c9;
        color: #c92a2a;
        border-radius: 8px;
        padding: 8px 12px;
        font-size: 0.78rem;
    }
    .empty-hint {
        margin: 10px 0 0;
        font-size: 0.85rem;
        color: #adb5bd;
    }
    .file-list {
        list-style: none;
        margin: 12px 0 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .file-row {
        display: flex;
        align-items: center;
        gap: 6px;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        background: #fff;
    }
    .file-info {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 10px;
        border: none;
        background: transparent;
        padding: 9px 12px;
        cursor: pointer;
        color: #343a40;
        text-align: left;
        min-width: 0;
    }
    .file-info:hover {
        color: var(--cs2-accent, #4263eb);
    }
    .file-name {
        flex: 1;
        font-size: 0.86rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .file-size {
        font-size: 0.74rem;
        color: #adb5bd;
        flex-shrink: 0;
    }
    .file-info :global(.download-icon) {
        opacity: 0;
        flex-shrink: 0;
        transition: opacity 0.15s ease;
    }
    .file-info:hover :global(.download-icon) {
        opacity: 1;
    }
    .file-remove {
        flex-shrink: 0;
        border: none;
        background: transparent;
        color: #adb5bd;
        padding: 8px 10px;
        cursor: pointer;
    }
    .file-remove:hover {
        color: #fa5252;
    }
</style>
